<template>
  <div class="container py-4 peliculas-page">
    <div class="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
      <div>
        <h2 class="mb-1">
          <i class="bi bi-film me-2 text-warning"></i>Películas y series
        </h2>
        <small class="text-muted">
          <template v-if="grupo">Catálogo de <strong>{{ grupo.nombre }}</strong></template>
          <template v-else>Tu lista. Crea un grupo familiar para compartirla.</template>
        </small>
      </div>
    </div>

    <div class="card shadow-sm mb-4 peliculas-add">
      <div class="card-body">
        <h3 class="h6 mb-2">
          <i class="bi bi-magic me-1"></i>Añadir título
        </h3>
        <p class="text-muted small mb-3">
          Escribe el nombre. Si hay varias con el mismo título, eliges la correcta
          y entonces se añade. Con FilmAffinity activo se rellenan formato, tipo, director,
          resumen y la <strong>puntuación</strong>.
        </p>
        <form class="row g-2 align-items-end" @submit.prevent="anadir">
          <div class="col-12 col-md-5">
            <label class="form-label small mb-1">Título</label>
            <input
              v-model="form.titulo"
              type="text"
              class="form-control"
              maxlength="180"
              required
              placeholder="Ej: Interestelar, The Bear…"
              :disabled="guardando"
            />
          </div>
          <div class="col-6 col-md-2">
            <label class="form-label small mb-1">Formato</label>
            <select v-model="form.tipo" class="form-select" :disabled="guardando">
              <option value="">Auto</option>
              <option value="pelicula">Película</option>
              <option value="serie">Serie</option>
            </select>
          </div>
          <div class="col-6 col-md-2">
            <div class="d-flex gap-2">
              <button class="btn btn-warning flex-grow-1" type="submit" :disabled="guardando || !form.titulo.trim()">
                <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="bi bi-plus-lg me-1"></i>
                {{ guardando ? (form.buscar_filmaffinity ? 'Buscando datos…' : 'Guardando…') : 'Añadir' }}
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary"
                :disabled="guardando || (!form.titulo.trim() && !candidatos.length && !errorAlta && !mensaje)"
                @click="atrasAlta"
              >
                <i class="bi bi-arrow-left"></i>
                <span class="d-none d-sm-inline ms-1">Atrás</span>
              </button>
            </div>
          </div>
          <div class="col-12">
            <label class="form-label small mb-1">Notas</label>
            <input
              v-model="form.notas"
              type="text"
              class="form-control"
              maxlength="500"
              placeholder="Ej: Recomendada por Ana, la vimos en el cine…"
              :disabled="guardando"
            />
          </div>
          <div class="col-12 col-md-6">
            <div class="form-check">
              <input id="peli-alta-fa" v-model="form.buscar_filmaffinity" class="form-check-input" type="checkbox" :disabled="guardando" />
              <label class="form-check-label small" for="peli-alta-fa">
                Descargar información de FilmAffinity
              </label>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <div class="form-check">
              <input id="peli-alta-privada" v-model="form.privada" class="form-check-input" type="checkbox" :disabled="guardando" />
              <label class="form-check-label small" for="peli-alta-privada">
                Privada — solo la ves tú, no se comparte con el grupo
              </label>
            </div>
          </div>
        </form>
        <div v-if="errorAlta" class="alert alert-danger py-2 small mt-3 mb-0">{{ errorAlta }}</div>
        <div v-if="mensaje" class="alert alert-success py-2 small mt-3 mb-0">{{ mensaje }}</div>
        <div v-if="candidatos.length" class="mt-3">
          <p class="small fw-semibold mb-2">
            Hay {{ candidatos.length }} coincidencias. Elige la correcta para añadirla:
          </p>
          <div class="list-group peli-candidatos">
            <button
              v-for="c in candidatos"
              :key="c.id"
              type="button"
              class="list-group-item list-group-item-action d-flex gap-3 align-items-start"
              :disabled="guardando"
              @click="elegirCandidato(c)"
            >
              <img v-if="posterSrc(c)" class="peli-cand-poster" :src="posterSrc(c)" :alt="c.titulo" />
              <div v-else class="peli-cand-poster" aria-hidden="true"></div>
              <div class="flex-grow-1 text-start">
                <div class="fw-semibold">{{ c.titulo }}</div>
                <div class="small text-muted">
                  {{ [c.anio, c.tipo === 'serie' ? 'Serie' : c.tipo === 'pelicula' ? 'Película' : null, c.director].filter(Boolean).join(' · ') }}
                </div>
              </div>
              <span v-if="c.filmaffinity_puntuacion != null" class="badge bg-warning text-dark align-self-center">
                FA {{ formatFa(c.filmaffinity_puntuacion) }}
              </span>
            </button>
          </div>
          <button type="button" class="btn btn-outline-secondary btn-sm mt-1" :disabled="guardando" @click="atrasAlta">
            <i class="bi bi-arrow-left me-1"></i>Atrás
          </button>
        </div>
      </div>
    </div>

    <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
      <div class="form-check mb-0">
        <input
          id="peli-solo-privadas"
          v-model="soloPrivadas"
          class="form-check-input"
          type="checkbox"
          @change="cargar"
        />
        <label class="form-check-label" for="peli-solo-privadas">
          Ver solo catálogo privado
        </label>
      </div>
      <div class="d-flex align-items-center gap-2 ms-auto">
        <label class="small text-muted mb-0" for="peli-orden">Orden</label>
        <select id="peli-orden" v-model="orden" class="form-select form-select-sm" style="width: auto;" @change="cambiarOrden">
          <option value="fecha">Fecha añadida</option>
          <option value="filmaffinity">Puntuación FilmAffinity</option>
        </select>
      </div>
    </div>

    <div class="d-flex flex-wrap gap-2 mb-2 align-items-center">
      <button
        v-for="op in estadosFiltro"
        :key="op.id"
        type="button"
        class="btn btn-sm"
        :class="estado === op.id ? 'btn-dark' : 'btn-outline-secondary'"
        @click="cambiarEstado(op.id)"
      >
        {{ op.label }}
      </button>
      <span class="vr mx-1 d-none d-sm-inline"></span>
      <button
        type="button"
        class="btn btn-sm"
        :class="tipo === '' ? 'btn-dark' : 'btn-outline-secondary'"
        @click="cambiarTipo('')"
      >
        Todas
      </button>
      <button
        type="button"
        class="btn btn-sm"
        :class="tipo === 'pelicula' ? 'btn-dark' : 'btn-outline-secondary'"
        @click="cambiarTipo('pelicula')"
      >
        Películas
      </button>
      <button
        type="button"
        class="btn btn-sm"
        :class="tipo === 'serie' ? 'btn-dark' : 'btn-outline-secondary'"
        @click="cambiarTipo('serie')"
      >
        Series
      </button>
    </div>
    <div v-if="filtroDistintoDelDefault" class="mb-3">
      <button type="button" class="btn btn-sm btn-outline-primary" :disabled="guardandoPrefs" @click="guardarFiltroDefault">
        <i class="bi bi-bookmark-check me-1"></i>
        {{ guardandoPrefs ? 'Guardando…' : 'Guardar filtro por defecto' }}
      </button>
      <span v-if="mensajePrefs" class="small text-success ms-2">{{ mensajePrefs }}</span>
    </div>

    <div class="input-group mb-4">
      <span class="input-group-text bg-white"><i class="bi bi-search"></i></span>
      <input
        v-model="q"
        type="search"
        class="form-control"
        placeholder="Buscar por título, director o tipo"
        @keyup.enter="cargar"
      />
      <button class="btn btn-outline-secondary" type="button" @click="cargar" :disabled="cargando">
        Buscar
      </button>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-if="cargando && !items.length" class="text-center py-5">
      <div class="spinner-border text-warning" role="status"></div>
      <p class="text-muted mt-2">Cargando el catálogo familiar…</p>
    </div>

    <div v-else-if="!items.length" class="text-center py-5 text-muted">
      <i class="bi bi-camera-reels fs-1 d-block mb-2"></i>
      <p class="mb-0">
        No hay {{
          estado === 'pendiente'
            ? 'títulos sin empezar'
            : estado === 'descartada'
              ? 'títulos descartados'
              : 'títulos'
        }}{{ soloPrivadas ? ' en tu catálogo privado' : '' }}
        {{ q ? ` para «${q}»` : '' }}.
      </p>
    </div>

    <div v-else class="peli-lista">
      <button
        v-for="item in items"
        :key="item.id"
        type="button"
        class="peli-row"
        :class="{ 'peli-row--pinned': item.pinned }"
        @click="abrir(item)"
      >
        <img
          v-if="posterSrc(item)"
          class="peli-row-poster"
          :src="posterSrc(item)"
          :alt="item.titulo"
          loading="lazy"
          decoding="async"
        />
        <div v-else class="peli-row-poster peli-row-poster--empty" aria-hidden="true"></div>
        <div class="peli-row-body">
          <div class="peli-row-title">
            <i v-if="item.pinned" class="bi bi-pin-angle-fill text-warning me-1" title="Fijada"></i>
            {{ item.titulo }}
            <span v-if="item.privada" class="peli-row-lock" title="Privada">🔒</span>
          </div>
          <div class="peli-row-meta">
            {{ compactMeta(item) }}
          </div>
        </div>
        <div class="peli-row-aside">
          <span v-if="item.filmaffinity_puntuacion != null" class="peli-fa-mini">
            {{ formatFa(item.filmaffinity_puntuacion) }}
          </span>
          <span class="badge rounded-pill bg-light text-dark">{{ etiquetaTipo(item.tipo) }}</span>
        </div>
      </button>
    </div>

    <div v-if="detalle" class="peli-modal" @click.self="atrasFicha">
      <div class="peli-modal-card">
        <div class="d-flex align-items-center gap-2 mb-3">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="atrasFicha">
            <i class="bi bi-arrow-left me-1"></i>Atrás
          </button>
          <button
            v-if="!edicion"
            type="button"
            class="btn btn-outline-dark btn-sm"
            :disabled="actualizando"
            @click="empezarEdicion"
          >
            <i class="bi bi-pencil me-1"></i>Editar
          </button>
          <button
            v-if="!edicion"
            type="button"
            class="btn btn-outline-primary btn-sm"
            :disabled="actualizando"
            title="Vuelve a buscar carátula y datos en FilmAffinity"
            @click="actualizarFicha"
          >
            <i class="bi bi-arrow-clockwise me-1" :class="{ 'peli-spin': actualizando }"></i>Actualizar ficha
          </button>
          <button
            v-if="!edicion"
            type="button"
            class="btn btn-sm"
            :class="detalle.pinned ? 'btn-warning' : 'btn-outline-warning'"
            :disabled="actualizando"
            :title="detalle.pinned ? 'Quitar de arriba' : 'Fijar arriba'"
            @click="togglePin"
          >
            <i class="bi" :class="detalle.pinned ? 'bi-pin-angle-fill' : 'bi-pin-angle'"></i>
            {{ detalle.pinned ? 'Fijada' : 'Fijar' }}
          </button>
          <button type="button" class="btn-close ms-auto" aria-label="Cerrar" @click="cerrar"></button>
        </div>
        <div v-if="mensajeDetalle" class="alert alert-success py-2 small">{{ mensajeDetalle }}</div>
        <div v-if="errorDetalle" class="alert alert-danger py-2 small">{{ errorDetalle }}</div>
        <div class="row g-3">
          <div class="col-md-4">
            <img
              v-if="posterSrc(detalle)"
              class="peli-poster peli-poster--lg"
              :src="posterSrc(detalle)"
              :alt="detalle.titulo"
            />
            <div v-else class="peli-poster peli-poster--lg" aria-hidden="true"></div>
          </div>
          <div class="col-md-8">
            <div class="d-flex flex-wrap gap-2 mb-2">
              <span class="badge bg-dark">{{ etiquetaTipo(detalle.tipo) }}</span>
              <span v-if="detalle.categoria" class="badge bg-secondary">{{ detalle.categoria }}</span>
              <span
                class="badge"
                :class="{
                  'bg-success': detalle.estado === 'vista',
                  'bg-dark': detalle.estado === 'descartada',
                  'bg-secondary': detalle.estado !== 'vista' && detalle.estado !== 'descartada',
                }"
              >
                {{ etiquetaEstado(detalle.estado) }}
              </span>
              <a
                v-if="detalle.filmaffinity_url"
                :href="detalle.filmaffinity_url"
                target="_blank"
                rel="noopener"
                class="badge bg-warning text-dark text-decoration-none"
              >
                FilmAffinity {{ formatFa(detalle.filmaffinity_puntuacion) || '' }}
              </a>
              <span v-else-if="detalle.filmaffinity_puntuacion != null" class="badge bg-warning text-dark">
                FilmAffinity {{ formatFa(detalle.filmaffinity_puntuacion) }}
              </span>
            </div>
            <div v-if="edicion" class="mb-2">
              <label class="form-label small mb-1">Título</label>
              <input
                ref="tituloInput"
                v-model="edicion.titulo"
                class="form-control form-control-lg fw-semibold"
                maxlength="180"
                required
              />
            </div>
            <h3 v-else class="h4 mb-1 d-flex align-items-center gap-2 flex-wrap">
              <span>{{ detalle.titulo }}</span>
              <button
                type="button"
                class="btn btn-link btn-sm p-0 text-decoration-none"
                title="Editar título"
                :disabled="actualizando"
                @click="empezarEdicionTitulo"
              >
                <i class="bi bi-pencil"></i>
              </button>
            </h3>
            <p class="text-muted mb-1">{{ metaDetalle(detalle) }}</p>
            <p class="small text-muted mb-3">
              Añadida por <strong>{{ detalle.creado_por_nombre || 'alguien' }}</strong>
              <template v-if="detalle.created_at"> el {{ formatFechaHora(detalle.created_at) }}</template>
              <span v-if="detalle.privada" class="ms-2 badge bg-secondary">Privada</span>
            </p>
            <form v-if="edicion" class="mb-3" @submit.prevent="guardarEdicion">
              <div class="row g-2">
                <div class="col-6">
                  <label class="form-label small mb-1">Formato</label>
                  <select v-model="edicion.tipo" class="form-select form-select-sm">
                    <option value="pelicula">Película</option>
                    <option value="serie">Serie</option>
                  </select>
                </div>
                <div class="col-6">
                  <label class="form-label small mb-1">Tipo</label>
                  <select v-model="edicion.categoria" class="form-select form-select-sm">
                    <option value="">—</option>
                    <option v-for="c in categorias" :key="c" :value="c">{{ c }}</option>
                  </select>
                </div>
                <div class="col-8">
                  <label class="form-label small mb-1">Director</label>
                  <input v-model="edicion.director" class="form-control form-control-sm" />
                </div>
                <div class="col-4">
                  <label class="form-label small mb-1">Año</label>
                  <input v-model="edicion.anio" class="form-control form-control-sm" type="number" min="1888" max="2100" />
                </div>
                <div class="col-12">
                  <label class="form-label small mb-1">Resumen</label>
                  <textarea v-model="edicion.sinopsis" class="form-control form-control-sm" rows="3"></textarea>
                </div>
                <div class="col-8">
                  <label class="form-label small mb-1">Enlace FilmAffinity</label>
                  <input v-model="edicion.filmaffinity_url" class="form-control form-control-sm" />
                </div>
                <div class="col-4">
                  <label class="form-label small mb-1">Nota FA</label>
                  <input v-model="edicion.filmaffinity_puntuacion" class="form-control form-control-sm" type="number" min="0" max="10" step="0.1" />
                </div>
                <div class="col-6">
                  <label class="form-label small mb-1">Fecha finalizado</label>
                  <input v-model="edicion.fecha_vista" class="form-control form-control-sm" type="date" />
                </div>
                <div class="col-12">
                  <label class="form-label small mb-1">Notas</label>
                  <textarea
                    v-model="edicion.notas"
                    class="form-control form-control-sm"
                    rows="2"
                    maxlength="500"
                    placeholder="Ej: Recomendada por…"
                  ></textarea>
                </div>
                <div v-if="soyAutor" class="col-12">
                  <div class="form-check">
                    <input id="peli-edit-privada" v-model="edicion.privada" class="form-check-input" type="checkbox" />
                    <label class="form-check-label small" for="peli-edit-privada">
                      Privada — no se comparte con el grupo familiar
                    </label>
                  </div>
                </div>
                <div class="col-12 d-flex gap-2">
                  <button type="submit" class="btn btn-sm btn-dark" :disabled="actualizando">Guardar</button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="atrasFicha">Atrás</button>
                </div>
              </div>
            </form>
            <template v-else>
            <p v-if="detalle.sinopsis" class="mb-3">{{ detalle.sinopsis }}</p>
            <dl class="row small mb-3 peli-props">
              <dt class="col-sm-4">Formato</dt>
              <dd class="col-sm-8">{{ etiquetaTipo(detalle.tipo) }}</dd>
              <dt class="col-sm-4">Tipo</dt>
              <dd class="col-sm-8">{{ detalle.categoria || '—' }}</dd>
              <dt class="col-sm-4">Estado</dt>
              <dd class="col-sm-8">{{ etiquetaEstado(detalle.estado) }}</dd>
              <dt class="col-sm-4">Director</dt>
              <dd class="col-sm-8">{{ detalle.director || '—' }}</dd>
              <dt class="col-sm-4">Resumen</dt>
              <dd class="col-sm-8">{{ detalle.sinopsis || '—' }}</dd>
              <dt class="col-sm-4">Enlace</dt>
              <dd class="col-sm-8">
                <a
                  v-if="detalle.filmaffinity_url"
                  :href="detalle.filmaffinity_url"
                  target="_blank"
                  rel="noopener"
                >FilmAffinity</a>
                <span v-else>—</span>
              </dd>
              <dt class="col-sm-4">Fecha finalizado</dt>
              <dd class="col-sm-8">{{ detalle.fecha_vista || '—' }}</dd>
              <dt class="col-sm-4">FilmAffinity</dt>
              <dd class="col-sm-8">{{ formatFa(detalle.filmaffinity_puntuacion) || '—' }}</dd>
              <dt class="col-sm-4">Notas</dt>
              <dd class="col-sm-8">{{ detalle.notas || '—' }}</dd>
              <template v-if="soyAutor">
              <dt class="col-sm-4">Privada</dt>
              <dd class="col-sm-8">
                <div class="form-check">
                  <input
                    id="peli-privada"
                    class="form-check-input"
                    type="checkbox"
                    :checked="detalle.privada"
                    :disabled="actualizando"
                    @change="togglePrivada"
                  />
                  <label class="form-check-label" for="peli-privada">
                    {{ detalle.privada ? 'Solo tú la ves' : 'Se comparte con el grupo' }}
                  </label>
                </div>
              </dd>
              </template>
            </dl>
            </template>

            <div class="mb-3">
              <label class="form-label small fw-semibold">Estado familiar</label>
              <div class="d-flex flex-wrap gap-2">
                <button
                  v-for="op in estadosAccion"
                  :key="op.id"
                  type="button"
                  class="btn btn-sm"
                  :class="detalle.estado === op.id ? 'btn-dark' : 'btn-outline-secondary'"
                  :disabled="actualizando"
                  @click="cambiarEstadoItem(op.id)"
                >
                  {{ op.label }}
                </button>
              </div>
            </div>

            <div class="border rounded p-3 bg-light mb-3">
              <p class="small fw-semibold mb-2">Tu nota</p>
              <div class="peli-stars mb-2" role="group" aria-label="Nota de 1 a 5">
                <button
                  v-for="n in 5"
                  :key="n"
                  type="button"
                  class="peli-star"
                  :class="{ on: miNota >= n }"
                  :disabled="actualizando"
                  :aria-label="`${n} estrellas`"
                  @click="ponerNota(n)"
                >★</button>
              </div>
              <p class="small text-muted mb-2">{{ formatEstrellas(miNota) }}</p>
              <div class="d-flex flex-wrap gap-2 align-items-center">
                <button
                  v-for="op in estadosAccion"
                  :key="`yo-${op.id}`"
                  type="button"
                  class="btn btn-sm"
                  :class="estadoPropio === op.id ? (op.id === 'descartada' ? 'btn-dark' : 'btn-success') : 'btn-outline-secondary'"
                  :disabled="actualizando"
                  @click="marcarYo(op.id)"
                >
                  {{ op.label }}
                </button>
              </div>
              <p class="small text-muted mt-2 mb-0">
                Tu estado: {{ etiquetaEstado(estadoPropio) }}
              </p>
            </div>

            <div v-if="detalle.miembros.length" class="mb-3">
              <p class="small fw-semibold mb-1">Familia</p>
              <ul class="list-unstyled small mb-0">
                <li v-for="m in detalle.miembros" :key="m.usuario_id">
                  Nota {{ m.nombre }} — {{ formatEstrellas(m.puntuacion) }}
                  <span class="text-muted"> · {{ etiquetaEstado(m.estado) }}</span>
                </li>
              </ul>
            </div>

            <div class="d-flex flex-wrap gap-2">
              <a
                v-if="detalle.trailer_url"
                :href="detalle.trailer_url"
                class="btn btn-outline-dark btn-sm"
                target="_blank"
                rel="noopener"
              >
                Tráiler
              </a>
              <a
                v-if="detalle.imdb_url"
                :href="detalle.imdb_url"
                class="btn btn-outline-secondary btn-sm"
                target="_blank"
                rel="noopener"
              >
                IMDb
              </a>
              <button type="button" class="btn btn-outline-danger btn-sm ms-auto" :disabled="actualizando" @click="eliminar">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import axios from '../config/axios';
import {
  ESTADOS,
  CATEGORIAS,
  etiquetaTipo,
  etiquetaEstado,
  formatFa,
  formatEstrellas,
  formatFechaHora,
  urlPoster,
} from '../utils/peliculasForm';
import { aplicarFichaEnLista, estadoPropioDe, fichaConEstadoPropio } from '../utils/peliculasLista';

export default {
  name: 'Peliculas',
  setup() {
    const items = ref([]);
    const grupo = ref(null);
    const cargando = ref(false);
    const guardando = ref(false);
    const guardandoPrefs = ref(false);
    const actualizando = ref(false);
    const error = ref('');
    const errorAlta = ref('');
    const mensaje = ref('');
    const mensajeDetalle = ref('');
    const mensajePrefs = ref('');
    const errorDetalle = ref('');
    const estado = ref('pendiente');
    const tipo = ref('');
    const orden = ref('fecha');
    const preferencias = ref({ filtro_estado: 'pendiente', filtro_tipo: '', orden: 'fecha' });
    const prefsCargadas = ref(false);
    const q = ref('');
    const soloPrivadas = ref(false);
    const detalle = ref(null);
    const miNota = ref(null);
    const form = ref({ titulo: '', tipo: '', privada: false, buscar_filmaffinity: true, notas: '' });
    const candidatos = ref([]);
    const edicion = ref(null);
    const tituloInput = ref(null);
    const categorias = CATEGORIAS;
    const store = useStore();
    const usuarioId = computed(() => store.getters['auth/currentUser']?.id);
    const soyAutor = computed(() => {
      if (!detalle.value || usuarioId.value == null) return false;
      return parseInt(detalle.value.creado_por, 10) === parseInt(usuarioId.value, 10);
    });
    const filtroDistintoDelDefault = computed(() => {
      if (!prefsCargadas.value) return false;
      const prefs = preferencias.value;
      return estado.value !== prefs.filtro_estado || tipo.value !== (prefs.filtro_tipo || '');
    });
    const estadoPropio = computed(() => estadoPropioDe(detalle.value));
    let seqFicha = 0;

    const estadosFiltro = ESTADOS;
    const estadosAccion = [
      { id: 'pendiente', label: 'Sin empezar' },
      { id: 'viendo', label: 'Viendo' },
      { id: 'vista', label: 'Terminada' },
      { id: 'descartada', label: 'Descartada' },
    ];

    function aplicarPreferencias(prefs, { aplicarFiltros = false } = {}) {
      preferencias.value = {
        filtro_estado: prefs?.filtro_estado || 'pendiente',
        filtro_tipo: prefs?.filtro_tipo || '',
        orden: prefs?.orden || 'fecha',
      };
      prefsCargadas.value = true;
      if (aplicarFiltros) {
        estado.value = preferencias.value.filtro_estado;
        tipo.value = preferencias.value.filtro_tipo || '';
        orden.value = preferencias.value.orden || 'fecha';
      }
    }

    async function cargar({ usarPrefsServidor = false } = {}) {
      cargando.value = true;
      error.value = '';
      try {
        const params = {};
        if (!usarPrefsServidor) {
          params.estado = estado.value;
          params.orden = orden.value;
          params.tipo = tipo.value;
        }
        if (q.value.trim()) params.q = q.value.trim();
        if (soloPrivadas.value) params.solo_privadas = 1;
        const { data } = await axios.get('/api/peliculas', { params });
        items.value = data.items || [];
        grupo.value = data.grupo || null;
        if (data.preferencias) {
          aplicarPreferencias(data.preferencias, { aplicarFiltros: usarPrefsServidor });
        }
      } catch (err) {
        error.value = err.response?.data?.error || 'Error al cargar películas';
      } finally {
        cargando.value = false;
      }
    }

    function aplicarFichaLocal(data) {
      if (!data || data.id == null) return;
      if (detalle.value && detalle.value.id === data.id) {
        detalle.value = data;
        miNota.value = data.yo?.puntuacion ?? miNota.value;
      }
      items.value = aplicarFichaEnLista(items.value, data, {
        estado: estado.value,
        tipo: tipo.value,
        soloPrivadas: soloPrivadas.value,
      });
    }

    async function guardarFiltroDefault() {
      guardandoPrefs.value = true;
      mensajePrefs.value = '';
      try {
        const { data } = await axios.put('/api/peliculas/preferencias', {
          filtro_estado: estado.value,
          filtro_tipo: tipo.value,
          orden: orden.value,
        });
        preferencias.value = {
          filtro_estado: data.filtro_estado || 'pendiente',
          filtro_tipo: data.filtro_tipo || '',
          orden: data.orden || 'fecha',
        };
        mensajePrefs.value = 'Filtro guardado';
        setTimeout(() => { mensajePrefs.value = ''; }, 2500);
      } catch (err) {
        error.value = err.response?.data?.error || 'No se pudo guardar el filtro';
      } finally {
        guardandoPrefs.value = false;
      }
    }

    async function guardarFicha(payload) {
      const { data } = await axios.post('/api/peliculas', payload, { timeout: 60000 });
      candidatos.value = [];
      form.value = {
        titulo: '',
        tipo: form.value.tipo,
        privada: form.value.privada,
        buscar_filmaffinity: form.value.buscar_filmaffinity,
        notas: '',
      };
      mensaje.value = data.filmaffinity_puntuacion != null
        ? `Añadida con ${formatFa(data.filmaffinity_puntuacion)} en FilmAffinity.`
        : 'Añadida al catálogo familiar.';
      await cargar();
      abrir(data);
    }

    async function anadir() {
      errorAlta.value = '';
      mensaje.value = '';
      candidatos.value = [];
      guardando.value = true;
      try {
        await guardarFicha({
          titulo: form.value.titulo,
          tipo: form.value.tipo || undefined,
          privada: form.value.privada,
          buscar_filmaffinity: form.value.buscar_filmaffinity,
          notas: form.value.notas || undefined,
        });
      } catch (err) {
        if (err.response?.status === 409 && err.response.data?.necesita_eleccion) {
          candidatos.value = err.response.data.candidatos || [];
          errorAlta.value = '';
        } else {
          errorAlta.value = err.response?.data?.error || 'No se pudieron completar los datos';
        }
      } finally {
        guardando.value = false;
      }
    }

    async function elegirCandidato(c) {
      errorAlta.value = '';
      mensaje.value = '';
      guardando.value = true;
      try {
        await guardarFicha({
          titulo: c.titulo || form.value.titulo,
          tipo: c.tipo || form.value.tipo || undefined,
          filmaffinity_id: c.id,
          privada: form.value.privada,
          buscar_filmaffinity: true,
          notas: form.value.notas || undefined,
        });
      } catch (err) {
        errorAlta.value = err.response?.data?.error || 'No se pudo añadir la ficha elegida';
      } finally {
        guardando.value = false;
      }
    }

    function atrasAlta() {
      if (candidatos.value.length) {
        candidatos.value = [];
        errorAlta.value = '';
        return;
      }
      form.value = {
        titulo: '',
        tipo: form.value.tipo,
        privada: form.value.privada,
        buscar_filmaffinity: form.value.buscar_filmaffinity,
        notas: '',
      };
      errorAlta.value = '';
      mensaje.value = '';
    }

    function abrir(item) {
      detalle.value = item;
      edicion.value = null;
      mensajeDetalle.value = '';
      errorDetalle.value = '';
      miNota.value = item.yo?.puntuacion ?? null;
    }

    function cerrar() {
      detalle.value = null;
      edicion.value = null;
      mensajeDetalle.value = '';
      errorDetalle.value = '';
    }

    function atrasFicha() {
      if (edicion.value) {
        edicion.value = null;
        return;
      }
      cerrar();
    }

    function empezarEdicion() {
      if (!detalle.value) return;
      const d = detalle.value;
      edicion.value = {
        titulo: d.titulo,
        tipo: d.tipo,
        categoria: d.categoria || '',
        director: d.director || '',
        sinopsis: d.sinopsis || '',
        anio: d.anio || '',
        filmaffinity_url: d.filmaffinity_url || '',
        filmaffinity_puntuacion: d.filmaffinity_puntuacion ?? '',
        fecha_vista: d.fecha_vista || '',
        notas: d.notas || '',
        privada: Boolean(d.privada),
      };
    }

    async function empezarEdicionTitulo() {
      empezarEdicion();
      await nextTick();
      tituloInput.value?.focus?.();
      tituloInput.value?.select?.();
    }

    async function guardarEdicion() {
      if (!detalle.value || !edicion.value) return;
      actualizando.value = true;
      try {
        const { data } = await axios.put(`/api/peliculas/${detalle.value.id}`, {
          titulo: edicion.value.titulo,
          tipo: edicion.value.tipo,
          categoria: edicion.value.categoria || null,
          director: edicion.value.director,
          sinopsis: edicion.value.sinopsis,
          anio: edicion.value.anio || null,
          filmaffinity_url: edicion.value.filmaffinity_url,
          filmaffinity_puntuacion: edicion.value.filmaffinity_puntuacion === ''
            ? null
            : edicion.value.filmaffinity_puntuacion,
          fecha_vista: edicion.value.fecha_vista || null,
          notas: edicion.value.notas || '',
          privada: edicion.value.privada,
        });
        edicion.value = null;
        detalle.value = data;
        await cargar();
      } catch (err) {
        error.value = err.response?.data?.error || 'No se pudo guardar la ficha';
      } finally {
        actualizando.value = false;
      }
    }

    async function togglePrivada() {
      if (!detalle.value) return;
      actualizando.value = true;
      try {
        const { data } = await axios.put(`/api/peliculas/${detalle.value.id}`, {
          privada: !detalle.value.privada,
        });
        detalle.value = data;
        await cargar();
      } catch (err) {
        error.value = err.response?.data?.error || 'No se pudo cambiar la privacidad';
      } finally {
        actualizando.value = false;
      }
    }

    async function togglePin() {
      if (!detalle.value) return;
      actualizando.value = true;
      try {
        const { data } = await axios.patch(`/api/peliculas/${detalle.value.id}/pin`, {
          pinned: !detalle.value.pinned,
        });
        detalle.value = data;
        await cargar();
      } catch (err) {
        error.value = err.response?.data?.error || 'No se pudo fijar la ficha';
      } finally {
        actualizando.value = false;
      }
    }

    async function actualizarFicha() {
      if (!detalle.value) return;
      actualizando.value = true;
      mensajeDetalle.value = '';
      errorDetalle.value = '';
      try {
        const { data } = await axios.post(
          `/api/peliculas/${detalle.value.id}/actualizar`,
          {},
          { timeout: 60000 }
        );
        detalle.value = data;
        miNota.value = data.yo?.puntuacion ?? miNota.value;
        mensajeDetalle.value = data.poster_url
          ? 'Ficha actualizada (carátula y datos).'
          : 'Ficha actualizada. No se encontró carátula nueva.';
        await cargar();
      } catch (err) {
        errorDetalle.value = err.response?.data?.error || 'No se pudo actualizar la ficha';
      } finally {
        actualizando.value = false;
      }
    }

    async function cambiarEstadoItem(nuevo) {
      if (!detalle.value) return;
      const previa = detalle.value;
      const reqId = ++seqFicha;
      aplicarFichaLocal({ ...previa, estado: nuevo });
      actualizando.value = true;
      errorDetalle.value = '';
      try {
        const { data } = await axios.patch(`/api/peliculas/${previa.id}/estado`, { estado: nuevo });
        if (reqId !== seqFicha) return;
        aplicarFichaLocal(data);
      } catch (err) {
        if (reqId === seqFicha) {
          aplicarFichaLocal(previa);
          errorDetalle.value = err.response?.data?.error || 'No se pudo cambiar el estado';
        }
      } finally {
        if (reqId === seqFicha) actualizando.value = false;
      }
    }

    async function marcarYo(nuevo, nota = miNota.value) {
      if (!detalle.value) return;
      const previa = detalle.value;
      const reqId = ++seqFicha;
      aplicarFichaLocal(fichaConEstadoPropio(previa, usuarioId.value, nuevo, nota));
      actualizando.value = true;
      errorDetalle.value = '';
      try {
        const { data } = await axios.patch(`/api/peliculas/${previa.id}/miembro`, {
          estado: nuevo,
          puntuacion: nota,
        });
        if (reqId !== seqFicha) return;
        aplicarFichaLocal(data);
      } catch (err) {
        if (reqId === seqFicha) {
          aplicarFichaLocal(previa);
          errorDetalle.value = err.response?.data?.error || 'No se pudo guardar tu marca';
        }
      } finally {
        if (reqId === seqFicha) actualizando.value = false;
      }
    }

    async function ponerNota(n) {
      miNota.value = n;
      await marcarYo(estadoPropioDe(detalle.value), n);
    }

    async function eliminar() {
      if (!detalle.value) return;
      if (!window.confirm(`¿Eliminar «${detalle.value.titulo}» del catálogo familiar?`)) return;
      actualizando.value = true;
      try {
        await axios.delete(`/api/peliculas/${detalle.value.id}`);
        cerrar();
        await cargar();
      } catch (err) {
        error.value = err.response?.data?.error || 'No se pudo eliminar';
      } finally {
        actualizando.value = false;
      }
    }

    function cambiarEstado(id) {
      estado.value = id;
      cargar();
    }

    function cambiarTipo(id) {
      tipo.value = id;
      cargar();
    }

    function cambiarOrden() {
      cargar();
    }

    function posterSrc(item) {
      return urlPoster(item?.poster_url);
    }

    function compactMeta(item) {
      const bits = [];
      if (item.anio) bits.push(item.anio);
      bits.push(etiquetaTipo(item.tipo));
      if (item.creado_por_nombre) bits.push(`añadida por ${item.creado_por_nombre}`);
      if (item.director) bits.push(item.director);
      if (item.categoria) bits.push(item.categoria);
      const notas = notasFamilia(item).slice(0, 2);
      for (const m of notas) {
        bits.push(`${m.nombre} ${formatEstrellas(m.puntuacion)}`);
      }
      return bits.join(' · ');
    }

    function metaDetalle(item) {
      const bits = [];
      if (item.anio) bits.push(item.anio);
      if (item.director) bits.push(item.director);
      if (item.duracion_min) bits.push(`${item.duracion_min} min`);
      if (item.temporadas) bits.push(`${item.temporadas} temp.`);
      if (item.pais) bits.push(item.pais);
      return bits.join(' · ');
    }

    function notasFamilia(item) {
      return (item.miembros || []).filter((m) => m.puntuacion != null);
    }

    onMounted(() => {
      cargar({ usarPrefsServidor: true });
    });

    return {
      items,
      grupo,
      cargando,
      guardando,
      guardandoPrefs,
      actualizando,
      error,
      errorAlta,
      mensaje,
      mensajeDetalle,
      mensajePrefs,
      errorDetalle,
      estado,
      tipo,
      orden,
      q,
      soloPrivadas,
      detalle,
      miNota,
      form,
      candidatos,
      edicion,
      tituloInput,
      categorias,
      soyAutor,
      filtroDistintoDelDefault,
      estadosFiltro,
      estadosAccion,
      estadoPropio,
      cargar,
      guardarFiltroDefault,
      anadir,
      elegirCandidato,
      atrasAlta,
      atrasFicha,
      empezarEdicion,
      empezarEdicionTitulo,
      guardarEdicion,
      togglePrivada,
      togglePin,
      actualizarFicha,
      abrir,
      cerrar,
      cambiarEstado,
      cambiarTipo,
      cambiarOrden,
      cambiarEstadoItem,
      marcarYo,
      ponerNota,
      eliminar,
      posterSrc,
      compactMeta,
      metaDetalle,
      notasFamilia,
      etiquetaTipo,
      etiquetaEstado,
      formatFa,
      formatEstrellas,
      formatFechaHora,
    };
  },
};
</script>

<style scoped>
.peliculas-add {
  border: 1px solid #f0d78c;
}
.peli-lista {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.peli-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  border: 1px solid #ece7dc;
  border-radius: 10px;
  background: #fff;
  padding: 0.4rem 0.65rem;
  cursor: pointer;
  transition: background 0.12s ease, box-shadow 0.12s ease;
}
.peli-row:hover {
  background: #fffaf0;
  box-shadow: 0 2px 10px rgba(40, 30, 10, 0.08);
}
.peli-row--pinned {
  border-color: #f0d78c;
  background: #fffdf5;
}
.peli-row-poster {
  width: 40px;
  height: 58px;
  flex: 0 0 40px;
  object-fit: cover;
  border-radius: 5px;
  background: linear-gradient(160deg, #2b2418, #6b4f1d);
}
.peli-row-poster--empty {
  display: block;
}
.peli-row-body {
  min-width: 0;
  flex: 1 1 auto;
}
.peli-row-title {
  font-weight: 650;
  font-size: 0.95rem;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.peli-row-star {
  color: #f5c518;
  margin-left: 0.2rem;
}
.peli-row-lock {
  margin-left: 0.25rem;
  font-size: 0.78rem;
}
.peli-row-meta {
  color: #6c675c;
  font-size: 0.78rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.peli-row-aside {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
}
.peli-fa-mini {
  background: #f5c518;
  color: #111;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.12rem 0.45rem;
  font-size: 0.78rem;
  min-width: 2.4rem;
  text-align: center;
}
.peli-poster {
  width: 100%;
  min-height: 220px;
  object-fit: cover;
  border-radius: 12px;
  background: linear-gradient(160deg, #2b2418, #6b4f1d);
}
.peli-poster--lg {
  min-height: 280px;
}
.peli-modal {
  position: fixed;
  inset: 0;
  background: rgba(20, 16, 8, 0.55);
  z-index: 1050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 1.5rem 1rem;
  overflow: auto;
}
.peli-modal-card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 1.25rem;
  max-width: 860px;
  width: 100%;
}
.peli-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
}
.peli-stars {
  display: flex;
  gap: 0.15rem;
}
.peli-star {
  border: 0;
  background: transparent;
  color: #d4c4a0;
  font-size: 1.55rem;
  line-height: 1;
  padding: 0 0.05rem;
}
.peli-star.on {
  color: #f5c518;
}
.peli-star:disabled {
  opacity: 0.6;
}
.peli-cand-poster {
  width: 46px;
  height: 68px;
  flex: 0 0 46px;
  object-fit: cover;
  border-radius: 6px;
  background: linear-gradient(160deg, #2b2418, #6b4f1d);
}
@keyframes peli-spin {
  to { transform: rotate(360deg); }
}
.peli-spin {
  display: inline-block;
  animation: peli-spin 0.8s linear infinite;
}
</style>
